import React, { useState, createContext, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, User, GraduationCap, FileText, BookOpen,
  Menu, X, Sun, Moon, LogOut, ChevronRight
} from 'lucide-react';

const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);

const NAV_ITEMS = [
  { path: '/law-portal', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/law-portal/profile', label: 'Profile & Intake', icon: User },
  { path: '/law-portal/schools', label: 'School List', icon: GraduationCap },
  { path: '/law-portal/applications', label: 'Applications', icon: FileText },
  { path: '/law-portal/essays', label: 'Essays & Docs', icon: BookOpen },
];

export default function LawPortalLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('law_theme') === 'dark');

  useEffect(() => {
    localStorage.setItem('law_theme', dark ? 'dark' : 'light');
  }, [dark]);

  const handleLogout = () => { logout(); navigate('/sign-in'); };

  const isActive = (item) => item.exact
    ? location.pathname === item.path
    : location.pathname.startsWith(item.path);

  const bg = dark ? 'bg-[#0D0F11]' : 'bg-[#F7F6F3]';
  const sidebarBg = dark ? 'bg-[#141618]' : 'bg-white';
  const borderColor = dark ? 'border-[#232528]' : 'border-[#E8E6E1]';
  const textPrimary = dark ? 'text-[#F0EFEC]' : 'text-[#1A1A1A]';
  const textSecondary = dark ? 'text-[#8B8B8B]' : 'text-[#6B6B6B]';
  const textMuted = dark ? 'text-[#555]' : 'text-[#999]';
  const hoverBg = dark ? 'hover:bg-[#1C1E20]' : 'hover:bg-[#F2F1EE]';
  const activeBg = dark ? 'bg-[#1C1E20]' : 'bg-[#EDECEA]';
  const activeAccent = dark ? 'border-l-[#C5A55A]' : 'border-l-[#1A1A1A]';

  return (
    <ThemeContext.Provider value={{ dark, setDark, bg, sidebarBg, borderColor, textPrimary, textSecondary, textMuted, hoverBg, activeBg }}>
      <div className={`min-h-screen ${bg} flex`}>
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-56 ${sidebarBg} border-r ${borderColor} flex flex-col transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          {/* Logo */}
          <div className={`h-14 flex items-center px-5 border-b ${borderColor}`}>
            <Link to="/law-portal" className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[#1A1A1A] rounded flex items-center justify-center">
                <span className="text-white text-[9px] font-bold tracking-tighter">ILC</span>
              </div>
              <span className={`text-sm font-semibold tracking-tight ${textPrimary}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                Law Portal
              </span>
            </Link>
            <button className="ml-auto lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className={`h-4 w-4 ${textSecondary}`} />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
            {NAV_ITEMS.map(item => {
              const active = isActive(item);
              return (
                <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded text-[13px] transition-all border-l-2 ${
                    active
                      ? `${activeBg} ${activeAccent} ${textPrimary} font-medium`
                      : `border-l-transparent ${textSecondary} ${hoverBg}`
                  }`}
                  data-testid={`law-nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                  <item.icon className="h-4 w-4" strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className={`px-3 py-3 border-t ${borderColor} space-y-2`}>
            <button onClick={() => setDark(!dark)}
              className={`flex items-center gap-2 w-full px-3 py-1.5 rounded text-[11px] ${textMuted} ${hoverBg} transition-colors`}
              data-testid="theme-toggle">
              {dark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
              {dark ? 'Light mode' : 'Dark mode'}
            </button>
            <button onClick={handleLogout}
              className={`flex items-center gap-2 w-full px-3 py-1.5 rounded text-[11px] ${textMuted} ${hoverBg} transition-colors`}>
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
            <div className={`px-3 py-1 text-[10px] ${textMuted}`}>
              {user?.email}
            </div>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
          {/* Top bar */}
          <header className={`h-14 ${sidebarBg} border-b ${borderColor} flex items-center px-4 lg:px-6 sticky top-0 z-30`}>
            <button className="lg:hidden mr-3" onClick={() => setSidebarOpen(true)}>
              <Menu className={`h-5 w-5 ${textSecondary}`} />
            </button>
            <div className={`flex items-center gap-1.5 text-[11px] ${textMuted}`}>
              {NAV_ITEMS.filter(n => isActive(n)).map(n => (
                <span key={n.path} className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  <span className={textSecondary}>{n.label}</span>
                </span>
              ))}
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 overflow-y-auto">
            <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6">
              {children}
            </div>
          </main>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}
